// Admin broadcast push. Goes through the standard notifications route; the
// gateway injects X-Internal-Token for gateway-verified Admin sessions only,
// so this client never sees or sends the internal token.
import apiClient from './client';

export interface BroadcastPayload {
  title: string;
  body: string;
  url?: string;
  respectPreferences?: boolean;
  category?: string;
  dryRun: boolean;
  /** When set, scope the push to devices whose owner belongs to this church. */
  churchId?: string;
  /** When set, only these members' devices receive the push. */
  userIds?: string[];
  /** Admin who triggered the broadcast (recorded in the audit trail). */
  sentByUserId?: string;
  sentByName?: string;
}

/** Dry run reports audience size; a real send adds accepted/failed. */
export interface BroadcastResponse {
  success: boolean;
  dryRun: boolean;
  deviceCount: number;
  peopleCount: number;
  totalRegistered?: number;
  accepted?: number;
  failed?: number;
}

/** One row in the broadcast audit trail. */
export interface BroadcastHistoryEntry {
  id: string;
  churchId?: string;
  title: string;
  body: string;
  url?: string;
  audience: 'church' | 'members' | 'all';
  deviceCount: number;
  peopleCount: number;
  accepted: number;
  failed: number;
  sentByUserId?: string;
  sentByName?: string;
  sentAt: string;
}

interface HistoryResponse {
  success: boolean;
  items: BroadcastHistoryEntry[];
}

export const notificationsApi = {
  broadcast: async (payload: BroadcastPayload): Promise<BroadcastResponse> => {
    const response = await apiClient.post<BroadcastResponse>('/api/notifications/broadcast', payload);
    return response.data;
  },

  getHistory: async (churchId: string, limit = 20): Promise<BroadcastHistoryEntry[]> => {
    const response = await apiClient.get<HistoryResponse>('/api/notifications/broadcast/history', {
      params: { churchId, limit },
    });
    return response.data.items ?? [];
  },
};